// What each staff role may do, mirrored from the database.
//
// This file is a SECOND copy of a rule the database already enforces, which is
// exactly the kind of duplication that drifts. It exists anyway because RLS
// can only refuse — a coach who presses "Mark paid" gets a permission error
// from Postgres, not an explanation, and a UI that offers buttons it knows
// will fail is worse than one that hides them.
//
// So the rule is: this table is DERIVED, never invented. Every entry below
// corresponds to a policy in supabase/migrations/20260825000000_core.sql,
// where the write tiers are declared as three arrays:
//
//   admin_only  -> locations, staff, plans, rooms, class_templates,
//                  class_overrides, booking_policies, households
//   operational -> members, memberships, membership_freezes, bookings
//   financial   -> invoices, invoice_lines, payments
//
// admin_only and financial are writable by owner and admin; operational by all
// four roles. Changing a staff member's role is owner-only, enforced by the
// staff_role_guard trigger rather than a policy, because RLS cannot express a
// column-level rule.
//
// If you change the migration, change this, and the contract test in
// tests/permissions.test.js will tell you if you forgot.

export const ROLES = ['owner', 'admin', 'coach', 'front_desk'];

const ALL_STAFF = ROLES;
const ADMIN = ['owner', 'admin'];
const OWNER = ['owner'];

/**
 * action -> roles allowed, and the table whose policy it mirrors.
 *
 * Reads are deliberately absent: every staff member in an org can select every
 * table in that org, so there is nothing to gate on the read side.
 */
export const ACTIONS = {
  // Day to day. The front desk has to be able to do these or it cannot work.
  'members.write': { table: 'members', roles: ALL_STAFF },
  'memberships.write': { table: 'memberships', roles: ALL_STAFF },
  'holds.write': { table: 'membership_freezes', roles: ALL_STAFF },
  'bookings.write': { table: 'bookings', roles: ALL_STAFF },

  // Money. A part-time hire on a shared terminal does not get to rewrite it.
  'billing.write': { table: 'invoices', roles: ADMIN },
  'payments.write': { table: 'payments', roles: ADMIN },

  // Configuration: changing these changes what the business sells.
  'plans.write': { table: 'plans', roles: ADMIN },
  'policy.write': { table: 'booking_policies', roles: ADMIN },
  'schedule.write': { table: 'class_templates', roles: ADMIN },
  'classes.override': { table: 'class_overrides', roles: ADMIN },
  'rooms.write': { table: 'rooms', roles: ADMIN },
  'locations.write': { table: 'locations', roles: ADMIN },
  'staff.write': { table: 'staff', roles: ADMIN },
  'org.write': { table: 'orgs', roles: ADMIN },

  // Enforced by a trigger, not a policy: an admin could otherwise promote
  // themselves to owner.
  'staff.role': { table: 'staff', roles: OWNER, viaTrigger: true },
};

/**
 * May this role take this action?
 *
 * A null role means there is no session — the local demo, where there is no
 * server to refuse anything and no tenant to protect. Everything is allowed
 * there on purpose; pretending otherwise would make the demo lie about what
 * the product does.
 *
 * An UNKNOWN action returns false rather than true. A typo should hide a
 * button, which someone reports, rather than silently grant something.
 */
export function can(role, action) {
  if (role === null || role === undefined) return true;
  const rule = ACTIONS[action];
  if (!rule) return false;
  return rule.roles.includes(role);
}

/** The reason a refusal happened, for a UI that has to explain itself. */
export function why(role, action) {
  if (can(role, action)) return null;
  const rule = ACTIONS[action];
  if (!rule) return 'That action is not recognised.';
  const allowed = rule.roles.length === 1 ? 'the owner' : 'owners and admins';
  return `Only ${allowed} can do that.`;
}
